import { useEffect, useState, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../lib/api';
import { uploadFile } from '../lib/uploader';
import { useAuth } from '../contexts/AuthContext';
import StorageWheel from '../components/StorageWheel';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { Copy, ExternalLink, AlertTriangle, Zap, HardDrive, Clock, Layers, Pencil, UploadCloud, Trash2, Link as LinkIcon, Loader2, Lock, ShieldCheck, Sparkles } from 'lucide-react';
import { startRazorpayCheckout } from '../lib/razorpay';
import BrandSettings from '../components/BrandSettings';
import PortalBrandingDialog from '../components/PortalBrandingDialog';
import RewardTab from '../components/RewardTab';
import FeedbackTab from '../components/FeedbackTab';
import { useAppConfig } from '../contexts/AppConfigContext';
import { usePaidGate } from '../hooks/usePaidGate';
import { copyToClipboard } from '../lib/clipboard';

const PLAN_LABELS = {
    starter: 'Starter — 200 GB / mo',
    pro: 'Pro — 500 GB / mo',
    ultimate_byob: 'Ultimate BYOB — Unlimited',
    ultimate_managed: 'Ultimate Managed — 5 TB / mo',
};

const PLAN_QUOTA = { starter: 200, pro: 500, ultimate_byob: 5000, ultimate_managed: 5000 };
const ROLLOVER_CAP = { starter: 200, pro: 500, ultimate_byob: 0, ultimate_managed: 5000 };

export default function Dashboard() {
    const { user, refresh } = useAuth();
    const cfg = useAppConfig();
    const { gate } = usePaidGate();
    const [portals, setPortals] = useState([]);
    const [files, setFiles] = useState([]);
    const [topups, setTopups] = useState([]);
    const [byob, setByob] = useState({ endpoint_url: '', access_key_id: '', secret_access_key: '', bucket_name: '' });
    const [renameTarget, setRenameTarget] = useState(null); // {id, title}
    const [renameValue, setRenameValue] = useState('');
    const [renaming, setRenaming] = useState(false);
    const ownerFileInput = useRef(null);
    const [ownerUploads, setOwnerUploads] = useState([]);
    const [deleting, setDeleting] = useState(null);
    const [billingAutopay, setBillingAutopay] = useState(false);
    const [brandingTarget, setBrandingTarget] = useState(null); // portal being edited for per-portal branding
    const [effectiveUltimate, setEffectiveUltimate] = useState(false);
    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');

    useEffect(() => {
        const t = searchParams.get('tab');
        if (t) setActiveTab(t);
    }, [searchParams]);

    useEffect(() => {
        Promise.all([
            api.get('/portals').then((r) => setPortals(r.data)),
            api.get('/files').then((r) => setFiles(r.data)),
            api.get('/settings/plans').then((r) => setTopups(r.data.topups)),
            // Determine effective Ultimate access (real plan or active trial) — powers
            // the per-portal branding "Brand" button visibility.
            api.get('/settings/branding').then((r) => setEffectiveUltimate(!!r.data.can_edit_logo_and_brand_name)).catch(() => {}),
        ]).catch(() => {});
    }, []);

    const totalGb = useMemo(() => {
        if (!user) return 0;
        return (PLAN_QUOTA[user.plan_id] || 200) + (user.rollover_gb || 0) + (user.topup_gb || 0);
    }, [user]);

    const copySlug = async (slug) => {
        const url = `${window.location.origin}/p/${slug}`;
        const ok = await copyToClipboard(url);
        ok ? toast.success('Portal link copied') : toast.error('Could not copy link');
    };

    const createPortal = async () => {
        const { data } = await api.post('/portals', { title: 'New portal' });
        setPortals((p) => [...p, data]);
        toast.success('Portal created');
    };

    const openRename = (portal) => {
        setRenameTarget(portal);
        setRenameValue(portal.title || '');
    };

    const submitRename = async () => {
        if (!renameTarget) return;
        const title = renameValue.trim();
        if (!title) { toast.error('Title cannot be empty'); return; }
        setRenaming(true);
        try {
            const { data } = await api.patch(`/portals/${renameTarget.id}`, { title });
            setPortals((prev) => prev.map((p) => (p.id === data.id ? data : p)));
            toast.success('Portal renamed');
            setRenameTarget(null);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Rename failed');
        } finally {
            setRenaming(false);
        }
    };

    const saveByob = async () => {
        try {
            await api.put('/settings/byob', byob);
            toast.success('BYOB config saved');
            await refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        }
    };

    const buyTopup = async (topup_id) => {
        await startRazorpayCheckout({ type: 'topup', topup_id, autopay: false });
    };

    const changePlan = async (plan_id, autopay = false) => {
        await startRazorpayCheckout({ type: 'plan', plan_id, autopay });
    };

    const payNow = async () => {
        if (user?.manual_payment_link) {
            window.open(user.manual_payment_link, '_blank');
        } else {
            await startRazorpayCheckout({ type: 'plan', plan_id: user.plan_id, autopay: false });
        }
    };

    const downloadFile = async (id) => {
        try {
            const { data } = await api.get(`/files/${id}/download`);
            window.open(data.url, '_blank');
        } catch (e) {
            toast.error('Could not fetch download URL');
        }
    };

    const startOwnerUpload = async (fileList) => {
        for (const f of Array.from(fileList)) {
            const localId = `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 6)}`;
            const ctrl = uploadFile({
                file: f,
                ownerMode: true,
                onProgress: ({ pct }) => setOwnerUploads((prev) => prev.map((x) => (x.id === localId ? { ...x, pct } : x))),
                onStatus: (status) => setOwnerUploads((prev) => prev.map((x) => (x.id === localId ? { ...x, status } : x))),
            });
            setOwnerUploads((prev) => [...prev, { id: localId, name: f.name, size: f.size, pct: 0, status: 'queued', ctrl }]);
            try {
                await ctrl.promise;
                setOwnerUploads((prev) => prev.map((x) => (x.id === localId ? { ...x, status: 'completed', pct: 100 } : x)));
                toast.success(`${f.name} uploaded to R2`);
                const { data } = await api.get('/files');
                setFiles(data);
                await refresh();
            } catch (e) {
                setOwnerUploads((prev) => prev.map((x) => (x.id === localId ? { ...x, status: 'failed' } : x)));
                toast.error(`${f.name} failed: ${e.message || 'upload error'}`);
            }
        }
        setTimeout(() => {
            setOwnerUploads((prev) => prev.filter((x) => x.status !== 'completed'));
        }, 4000);
    };

    const copyShareLink = async (fileId) => {
        try {
            const { data } = await api.post(`/files/${fileId}/share`);
            const url = `${window.location.origin}/s/${data.share_token}`;
            const ok = await copyToClipboard(url);
            ok ? toast.success('Share link copied to clipboard') : toast.success('Share link ready');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not generate share link');
        }
    };

    const deleteFile = async (fileId, filename) => {
        if (!window.confirm(`Permanently delete "${filename}" from R2? This cannot be undone.`)) return;
        setDeleting(fileId);
        try {
            await api.delete(`/files/${fileId}`);
            setFiles((prev) => prev.filter((f) => f.id !== fileId));
            toast.success('File deleted');
            await refresh();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Delete failed');
        } finally {
            setDeleting(null);
        }
    };

    const deletePortal = async (portalId, title) => {
        if (!window.confirm(`Delete portal "${title}"? Files received through it stay in your Files tab. This cannot be undone.`)) return;
        try {
            await api.delete(`/portals/${portalId}`);
            setPortals((prev) => prev.filter((p) => p.id !== portalId));
            toast.success('Portal deleted');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Delete failed');
        }
    };

    if (!user) return null;

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="dashboard-root">
            <div className="flex flex-wrap items-start justify-between gap-6 mb-10">
                <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Dashboard</p>
                    <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-medium tracking-tight">
                        Welcome back, {user.name?.split(' ')[0]}
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Current plan: <span className="text-foreground">{PLAN_LABELS[user.plan_id]}</span>
                        {user.autopay_enabled && (
                            <span className="ml-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 border border-[#FF6B35]/40 text-[#FF6B35]">
                                <ShieldCheck className="h-3 w-3" /> Auto-renewal on
                            </span>
                        )}
                    </p>
                </div>
            </div>

            {/* Grace Period / Expired banner */}
            {user.plan_status === 'grace_period' && (
                <div data-testid="grace-banner" className="mb-8 border border-amber-500/50 bg-amber-500/10 p-5 flex flex-wrap items-start gap-4 justify-between">
                    <div className="flex gap-3 items-start flex-1 min-w-[280px]">
                        <Lock className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-amber-500 uppercase tracking-[0.15em] text-xs">7-Day Grace Period Active</p>
                            <p className="mt-2 text-sm leading-relaxed">
                                Your payment is overdue. Uploads are temporarily locked, but your existing files and rolled-over data are safe.
                                Complete payment before Day 7 to avoid a rollover reset.
                            </p>
                            {user.manual_payment_link_expires_at && (
                                <p className="mt-2 text-xs text-muted-foreground">
                                    Grace ends: {new Date(user.manual_payment_link_expires_at).toLocaleDateString()}
                                </p>
                            )}
                        </div>
                    </div>
                    <Button data-testid="pay-now-btn" onClick={payNow} className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white">
                        Pay now
                    </Button>
                </div>
            )}
            {user.plan_status === 'expired' && (
                <div data-testid="expired-banner" className="mb-8 border border-destructive/50 bg-destructive/10 p-5 flex flex-wrap items-start gap-4 justify-between">
                    <div className="flex gap-3 items-start flex-1 min-w-[280px]">
                        <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                        <div>
                            <p className="font-medium text-destructive uppercase tracking-[0.15em] text-xs">Subscription Expired</p>
                            <p className="mt-2 text-sm leading-relaxed">
                                Your 7-day grace period ended and the rollover vault has been reset. Uploads remain locked until you renew.
                                Downloads of existing files continue to work.
                            </p>
                        </div>
                    </div>
                    <Button data-testid="renew-btn" onClick={payNow} className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white">
                        Renew subscription
                    </Button>
                </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-8">
                <TabsList data-testid="dashboard-tabs" className="bg-card border border-border">
                    <TabsTrigger data-testid="tab-overview" value="overview">Overview</TabsTrigger>
                    <TabsTrigger data-testid="tab-portals" value="portals">Portals</TabsTrigger>
                    <TabsTrigger data-testid="tab-files" value="files">Files</TabsTrigger>
                    <TabsTrigger data-testid="tab-billing" value="billing">Billing</TabsTrigger>
                    <TabsTrigger data-testid="tab-rewards" value="rewards">Reward</TabsTrigger>
                    <TabsTrigger data-testid="tab-settings" value="settings">Settings</TabsTrigger>
                    <TabsTrigger data-testid="tab-feedback" value="feedback">Feedback</TabsTrigger>
                </TabsList>

                {/* Overview */}
                <TabsContent value="overview" className="space-y-6">
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        <Card className="p-8 border-border bg-card">
                            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-6">Active storage</p>
                            <div className="flex items-center justify-center">
                                <StorageWheel
                                    usedGb={cfg.beta_active ? ((user.used_storage_bytes || 0) / (1024 ** 3)) : (user.used_gb_this_cycle || 0)}
                                    totalGb={cfg.beta_active ? (user.allocated_storage_gb || cfg.beta_base_storage_gb) : totalGb}
                                    testId="dashboard-storage-wheel"
                                />
                            </div>
                        </Card>
                        <div className="grid gap-6 lg:col-span-2 grid-cols-1 sm:grid-cols-2">
                            <StatCard icon={HardDrive} label="Rollover vault" value={`${(user.rollover_gb || 0).toFixed(1)} / ${ROLLOVER_CAP[user.plan_id] || 0} GB`} />
                            <StatCard icon={Zap} label="One-time top-ups" value={`+${(user.topup_gb || 0).toFixed(0)} GB`} />
                            <StatCard icon={Layers} label="Active portals" value={String(portals.length)} />
                            <StatCard icon={Clock} label="Cycle ends" value={user.cycle_end ? new Date(user.cycle_end).toLocaleDateString() : '—'} />
                        </div>
                    </div>

                    {user.plan_id !== 'ultimate_byob' && (
                        <div data-testid="purge-banner" className="border border-amber-500/30 bg-amber-500/5 p-4 text-sm flex gap-3 items-start">
                            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                            <p className="text-amber-100 dark:text-amber-100 text-foreground">
                                <strong className="text-amber-500">Managed storage notice:</strong> files are permanently purged 7 days after upload for data privacy and liability protection.
                            </p>
                        </div>
                    )}
                </TabsContent>

                {/* Portals */}
                <TabsContent value="portals" className="space-y-4">
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">Share these URLs with your clients. No signup required on their side.</p>
                        <Button data-testid="create-portal-btn" onClick={gate(createPortal)} className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white">Create new portal</Button>
                    </div>
                    <div className="grid gap-4">
                        {portals.map((p) => (
                            <Card key={p.id} className="p-6 flex flex-wrap items-center justify-between gap-4" data-testid={`portal-row-${p.slug}`}>
                                <div>
                                    <p className="font-heading text-lg" data-testid={`portal-title-${p.slug}`}>{p.title}</p>
                                    <p className="text-xs text-muted-foreground mt-1 font-mono">/p/{p.slug}</p>
                                </div>
                                <div className="flex gap-2">
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button data-testid={`rename-portal-${p.slug}`} variant="outline" size="sm" onClick={gate(() => openRename(p))}>
                                                <Pencil className="h-4 w-4 mr-2" /> Rename
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Edit portal title</TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button data-testid={`copy-portal-${p.slug}`} variant="outline" size="sm" onClick={gate(() => copySlug(p.slug))}>
                                                <Copy className="h-4 w-4 mr-2" /> Copy link
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Copy link to clipboard</TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                data-testid={`brand-portal-${p.slug}`}
                                                variant="outline"
                                                size="sm"
                                                onClick={gate(() => setBrandingTarget(p))}
                                            >
                                                <Sparkles className="h-4 w-4 mr-2" /> Brand
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            {effectiveUltimate ? 'Customize branding for this client link' : 'Per-portal branding — Ultimate only'}
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                data-testid={`delete-portal-${p.slug}`}
                                                variant="ghost"
                                                size="icon"
                                                className="text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                                onClick={gate(() => deletePortal(p.id, p.title))}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>Delete portal permanently</TooltipContent>
                                    </Tooltip>
                                </div>
                            </Card>
                        ))}
                        {portals.length === 0 && (
                            <p className="text-sm text-muted-foreground">No portals yet — create one to get a shareable URL.</p>
                        )}
                    </div>
                </TabsContent>

                {/* Files */}
                <TabsContent value="files" className="space-y-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                            <p className="text-sm text-muted-foreground">All inbound uploads from your portals + outbound files you upload for sharing.</p>
                        </div>
                        <div className="flex gap-2">
                            <input
                                ref={ownerFileInput}
                                type="file"
                                multiple
                                className="hidden"
                                data-testid="owner-upload-input"
                                onChange={(e) => e.target.files && startOwnerUpload(e.target.files)}
                            />
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <span>
                                        <Button
                                            data-testid="owner-upload-btn"
                                            onClick={gate(() => ownerFileInput.current?.click())}
                                            disabled={user.plan_status === 'grace_period' || user.plan_status === 'expired'}
                                            className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white disabled:opacity-50"
                                        >
                                            {user.plan_status === 'grace_period' || user.plan_status === 'expired' ? (
                                                <><Lock className="h-4 w-4 mr-2" /> Locked</>
                                            ) : (
                                                <><UploadCloud className="h-4 w-4 mr-2" /> Upload file</>
                                            )}
                                        </Button>
                                    </span>
                                </TooltipTrigger>
                                <TooltipContent>
                                    {user.plan_status === 'grace_period'
                                        ? 'Uploads locked \u2014 complete payment to resume'
                                        : user.plan_status === 'expired'
                                        ? 'Subscription expired \u2014 renew to resume uploads'
                                        : 'Upload a file directly to your R2 bucket'}
                                </TooltipContent>
                            </Tooltip>
                        </div>
                    </div>

                    {ownerUploads.length > 0 && (
                        <div className="space-y-2" data-testid="owner-upload-progress">
                            {ownerUploads.map((u) => {
                                const isActive = ['queued', 'initiating', 'uploading', 'paused'].includes(u.status);
                                return (
                                    <div key={u.id} className="border border-border p-3 bg-card" data-testid={`upload-row-${u.id}`}>
                                        <div className="flex items-center justify-between text-xs gap-3">
                                            <span className="font-mono truncate flex-1">{u.name}</span>
                                            <span className="text-muted-foreground shrink-0">
                                                {(u.size / 1024 ** 3).toFixed(2)} GB · <span className={u.status === 'paused' ? 'text-amber-500' : ''}>{u.status}</span>
                                            </span>
                                            {isActive && u.ctrl && (
                                                u.status === 'paused' ? (
                                                    <button
                                                        data-testid={`upload-resume-${u.id}`}
                                                        onClick={() => u.ctrl.resume()}
                                                        className="text-[10px] uppercase tracking-[0.18em] border border-[#FF6B35] text-[#FF6B35] px-2.5 py-1 hover:bg-[#FF6B35] hover:text-white transition-colors rounded-md"
                                                    >
                                                        Resume
                                                    </button>
                                                ) : (
                                                    <button
                                                        data-testid={`upload-pause-${u.id}`}
                                                        onClick={() => u.ctrl.pause()}
                                                        className="text-[10px] uppercase tracking-[0.18em] border border-border text-muted-foreground px-2.5 py-1 hover:text-foreground hover:border-foreground transition-colors rounded-md"
                                                    >
                                                        Pause
                                                    </button>
                                                )
                                            )}
                                        </div>
                                        <div className="mt-2 h-1.5 bg-muted overflow-hidden rounded">
                                            <div
                                                className={`h-full transition-all ${u.status === 'paused' ? 'bg-amber-500' : 'bg-[#FF6B35]'}`}
                                                style={{ width: `${u.pct || 0}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {user.plan_id !== 'ultimate_byob' && (
                        <div data-testid="files-purge-banner" className="border border-amber-500/30 bg-amber-500/5 p-4 text-sm flex gap-3 items-start">
                            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
                            <p>
                                <strong className="text-amber-500">⚠️ Managed storage files are permanently purged 7 days after upload</strong> for data privacy and liability protection.
                            </p>
                        </div>
                    )}
                    <div className="border border-border">
                        <table className="w-full text-sm">
                            <thead className="bg-card border-b border-border">
                                <tr className="text-left uppercase tracking-[0.15em] text-xs text-muted-foreground">
                                    <th className="p-4">File</th>
                                    <th className="p-4">Source</th>
                                    <th className="p-4">Size</th>
                                    <th className="p-4">Status</th>
                                    <th className="p-4">Expires</th>
                                    <th className="p-4 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody data-testid="files-table">
                                {files.map((f) => (
                                    <tr key={f.id} className="border-b border-border last:border-0" data-testid={`file-row-${f.id}`}>
                                        <td className="p-4 font-mono text-xs">{f.filename}</td>
                                        <td className="p-4 text-xs">
                                            {f.kind === 'outbound' ? (
                                                <span className="inline-block px-2 py-0.5 bg-[#FF6B35]/10 text-[#FF6B35] uppercase tracking-[0.15em] text-[10px]">Outbound</span>
                                            ) : (f.client_name || '—')}
                                        </td>
                                        <td className="p-4">{(f.size_bytes / 1024 ** 3).toFixed(2)} GB</td>
                                        <td className="p-4">
                                            <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-[0.15em] ${
                                                f.status === 'completed' ? 'bg-[#FF6B35]/10 text-[#FF6B35]' :
                                                f.status === 'expired' ? 'bg-destructive/10 text-destructive' :
                                                'bg-muted text-muted-foreground'
                                            }`}>{f.status}</span>
                                        </td>
                                        <td className="p-4 text-xs text-muted-foreground">{f.expires_at ? new Date(f.expires_at).toLocaleString() : '—'}</td>
                                        <td className="p-4">
                                            <div className="flex items-center gap-1 justify-end">
                                                {f.status === 'completed' && (
                                                    <>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button data-testid={`share-file-${f.id}`} size="icon" variant="ghost" onClick={gate(() => copyShareLink(f.id))}>
                                                                    <LinkIcon className="h-4 w-4" />
                                                                </Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Copy share link to clipboard</TooltipContent>
                                                        </Tooltip>
                                                        <Tooltip>
                                                            <TooltipTrigger asChild>
                                                                <Button data-testid={`download-file-${f.id}`} size="sm" variant="outline" onClick={() => downloadFile(f.id)}>Download</Button>
                                                            </TooltipTrigger>
                                                            <TooltipContent>Get secure download link</TooltipContent>
                                                        </Tooltip>
                                                    </>
                                                )}
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            data-testid={`delete-file-${f.id}`}
                                                            size="icon"
                                                            variant="ghost"
                                                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                            disabled={deleting === f.id}
                                                            onClick={gate(() => deleteFile(f.id, f.filename))}
                                                        >
                                                            {deleting === f.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>Permanently delete from R2</TooltipContent>
                                                </Tooltip>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {files.length === 0 && (
                                    <tr><td className="p-6 text-muted-foreground text-center" colSpan={6}>No files yet. Upload one above or share your portal link to receive files.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                {/* Billing */}
                <TabsContent value="billing" className="space-y-8">
                    {/* Manual payment link surfaced during grace period */}
                    {user.plan_status === 'grace_period' && user.manual_payment_link && (
                        <Card className="p-6 border-[#FF6B35]/50 bg-[#FF6B35]/5" data-testid="manual-payment-link-card">
                            <p className="text-xs uppercase tracking-[0.2em] text-[#FF6B35]">Manual renewal link</p>
                            <h3 className="mt-2 font-heading text-2xl">Complete your renewal payment</h3>
                            <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                                Since Auto-Renewal is off, we generated a secure Razorpay payment link for you. Open it to complete this month&apos;s payment before the 7-day grace period ends.
                            </p>
                            <div className="mt-4 flex flex-wrap items-center gap-3">
                                <a href={user.manual_payment_link} target="_blank" rel="noreferrer">
                                    <Button data-testid="open-manual-payment-btn" className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white">
                                        <ExternalLink className="h-4 w-4 mr-2" /> Open payment link
                                    </Button>
                                </a>
                                <Button
                                    variant="outline"
                                    data-testid="copy-manual-payment-link"
                                    onClick={async () => {
                                        const ok = await copyToClipboard(user.manual_payment_link);
                                        ok ? toast.success('Payment link copied') : toast.error('Could not copy link');
                                    }}
                                >
                                    <Copy className="h-4 w-4 mr-2" /> Copy link
                                </Button>
                            </div>
                        </Card>
                    )}

                    <section>
                        <div className="flex items-center justify-between flex-wrap gap-3">
                            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Change plan</p>
                            <label className="text-xs flex items-center gap-2 text-muted-foreground cursor-pointer">
                                <input
                                    type="checkbox"
                                    data-testid="billing-autopay-toggle"
                                    checked={billingAutopay}
                                    onChange={(e) => setBillingAutopay(e.target.checked)}
                                    className="accent-[#FF6B35]"
                                />
                                Enable Auto-Renewal for next plan change
                            </label>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                            {['starter', 'pro', 'ultimate_byob', 'ultimate_managed'].map((pid) => (
                                <Card key={pid} className={`p-6 flex flex-col gap-3 ${user.plan_id === pid ? 'border-[#FF6B35]' : ''}`}>
                                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{PLAN_LABELS[pid].split(' \u2014 ')[0]}</p>
                                    <p className="font-heading text-2xl">{PLAN_LABELS[pid].split(' \u2014 ')[1]}</p>
                                    <Button
                                        data-testid={`change-plan-${pid}`}
                                        disabled={user.plan_id === pid && user.plan_status === 'active'}
                                        onClick={() => changePlan(pid, billingAutopay)}
                                        size="sm"
                                        className={user.plan_id === pid && user.plan_status === 'active' ? '' : 'bg-[#FF6B35] hover:bg-[#E55C2A] text-white'}
                                    >
                                        {user.plan_id === pid && user.plan_status === 'active' ? 'Current' : (billingAutopay ? 'Subscribe' : 'Buy 1 month')}
                                    </Button>
                                </Card>
                            ))}
                        </div>
                    </section>

                    <section>
                        <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">One-time top-ups</p>
                        <p className="text-sm text-muted-foreground mt-2 max-w-xl">Add capacity mid-month. Top-ups apply instantly and expire at the end of the current billing cycle.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
                            {topups.map((t) => (
                                <Card key={t.id} className="p-6">
                                    <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{t.name}</p>
                                    <p className="font-heading text-3xl mt-2">₹{t.price_inr || Math.round((t.price || 0) * 83)}</p>
                                    <Button data-testid={`buy-topup-${t.id}`} onClick={() => buyTopup(t.id)} size="sm" className="mt-4 bg-[#FF6B35] hover:bg-[#E55C2A] text-white">Buy now</Button>
                                </Card>
                            ))}
                        </div>
                    </section>

                    <section className="border border-border p-6 bg-card">
                        <p className="text-xs uppercase tracking-[0.2em] text-[#FF6B35]">Our Transparent Billing Promise</p>
                        <p className="mt-3 text-sm leading-relaxed">
                            We never force automatic charges. Unless you check &quot;Enable Auto-Renewal&quot;, you will pay manually via email. If a payment is missed, your account enters a 7-day Grace Period where your rolled-over data stays perfectly safe and intact. New uploads will be temporarily locked during this week, and files will reset if unpaid by Day 7.
                        </p>
                    </section>
                </TabsContent>

                {/* Reward (renamed from "Billing & Rewards") */}
                <TabsContent value="rewards" className="space-y-6">
                    <RewardTab />
                </TabsContent>

                {/* Settings */}
                <TabsContent value="settings" className="space-y-6">
                    {/* Tiered studio branding (Starter locked, Pro partial, Ultimate full) */}
                    <BrandSettings />

                    {user.plan_id === 'ultimate_byob' ? (
                        <Card className="p-8 border border-[#FF6B35]/40 bg-card">
                            <p className="text-xs uppercase tracking-[0.2em] text-[#FF6B35]">Ultimate BYOB (Bring Your Own Bucket) Tier</p>
                            <h3 className="mt-3 font-heading text-2xl">Bring your own storage bucket</h3>
                            <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                                Want to keep files forever? Connect your own storage bucket under settings to override the 7-day purge limit entirely.
                            </p>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                                <div className="space-y-2">
                                    <Label>Endpoint URL</Label>
                                    <Input data-testid="byob-endpoint" placeholder="https://<account>.r2.cloudflarestorage.com" value={byob.endpoint_url} onChange={(e) => setByob({ ...byob, endpoint_url: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Bucket name</Label>
                                    <Input data-testid="byob-bucket" placeholder="my-bucket" value={byob.bucket_name} onChange={(e) => setByob({ ...byob, bucket_name: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Access Key ID</Label>
                                    <Input data-testid="byob-access-key" value={byob.access_key_id} onChange={(e) => setByob({ ...byob, access_key_id: e.target.value })} />
                                </div>
                                <div className="space-y-2">
                                    <Label>Secret Access Key</Label>
                                    <Input data-testid="byob-secret-key" type="password" value={byob.secret_access_key} onChange={(e) => setByob({ ...byob, secret_access_key: e.target.value })} />
                                </div>
                            </div>
                            <Button data-testid="save-byob-btn" onClick={saveByob} className="mt-6 bg-[#FF6B35] hover:bg-[#E55C2A] text-white">Save configuration</Button>
                        </Card>
                    ) : (
                        <Card className="p-8 bg-card">
                            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Managed storage</p>
                            <h3 className="mt-3 font-heading text-2xl">You&apos;re on a fully-managed plan.</h3>
                            <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
                                Files stream to our corporate Cloudflare R2 partition — zero infrastructure setup on your side. Upgrade to <strong>Ultimate BYOB</strong> to connect your own bucket and remove the 7-day purge.
                            </p>
                        </Card>
                    )}
                </TabsContent>

                {/* Feedback (Tab 8) */}
                <TabsContent value="feedback" className="space-y-6">
                    <FeedbackTab />
                </TabsContent>
            </Tabs>

            {/* Rename portal dialog */}
            <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
                <DialogContent data-testid="rename-portal-dialog">
                    <DialogHeader>
                        <DialogTitle>Rename portal</DialogTitle>
                        <DialogDescription>
                            Give this portal a memorable name — it appears on the header of your public client page.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-2">
                        <Label htmlFor="rename-input">Portal title</Label>
                        <Input
                            id="rename-input"
                            data-testid="rename-portal-input"
                            value={renameValue}
                            onChange={(e) => setRenameValue(e.target.value)}
                            placeholder="e.g. Nike Fall 2026 — Raw Ingest"
                            onKeyDown={(e) => { if (e.key === 'Enter') submitRename(); }}
                            autoFocus
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRenameTarget(null)}>Cancel</Button>
                        <Button
                            data-testid="rename-portal-save"
                            className="bg-[#FF6B35] hover:bg-[#E55C2A] text-white"
                            onClick={submitRename}
                            disabled={renaming}
                        >
                            {renaming ? 'Saving…' : 'Save'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Per-portal branding dialog (Ultimate-effective only) */}
            <PortalBrandingDialog
                portal={brandingTarget}
                canEdit={effectiveUltimate}
                onClose={() => setBrandingTarget(null)}
                onSaved={(updated) => setPortals((prev) => prev.map((p) => p.id === updated.id ? updated : p))}
            />
        </div>
    );
}

function StatCard({ icon: Icon, label, value }) {
    return (
        <Card className="p-6 bg-card border-border">
            <div className="flex items-center gap-3">
                <div className="h-9 w-9 border border-border grid place-items-center">
                    <Icon className="h-4 w-4 text-[#FF6B35]" />
                </div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</p>
            </div>
            <p className="mt-4 font-heading text-2xl">{value}</p>
        </Card>
    );
}
